import * as THREE from 'three'

/**
 * Rooster tail snow spray effect when the skier carves a turn.
 * Particles spray up and to the side opposite the turn direction.
 */

const MAX_PARTICLES = 80
const PARTICLE_LIFE = 0.6     // seconds
const SPRAY_SPEED = 4          // base particle speed
const PARTICLE_SIZE = 0.08

interface SnowParticle {
  mesh: THREE.Mesh
  velocity: THREE.Vector3
  life: number
  maxLife: number
}

export class RoosterTail {
  private scene: THREE.Scene
  private particles: SnowParticle[] = []
  private particlePool: THREE.Mesh[] = []
  private snowMat: THREE.MeshBasicMaterial
  private spawnTimer = 0

  constructor(scene: THREE.Scene) {
    this.scene = scene
    this.snowMat = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.8,
    })

    // Pre-create particle pool
    const geo = new THREE.SphereGeometry(PARTICLE_SIZE, 4, 4)
    for (let i = 0; i < MAX_PARTICLES; i++) {
      const mesh = new THREE.Mesh(geo, this.snowMat.clone())
      mesh.visible = false
      scene.add(mesh)
      this.particlePool.push(mesh)
    }
  }

  update(
    dt: number,
    skierX: number,
    skierY: number,
    skierZ: number,
    isSteering: boolean,
    steerDirection: number,  // -1 = left, 0 = none, 1 = right
    speed: number,
    isAirborne: boolean,
  ): void {
    // Spawn particles when turning on the ground at speed
    this.spawnTimer -= dt
    if (isSteering && !isAirborne && speed > 2 && this.spawnTimer <= 0) {
      this.spawnTimer = 0.02  // spawn every 20ms

      const count = Math.floor(1 + speed * 0.15)  // more particles at higher speed
      for (let i = 0; i < count; i++) {
        this.spawnParticle(skierX, skierY, skierZ, steerDirection, speed)
      }
    }

    // Update existing particles
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i]
      p.life -= dt

      if (p.life <= 0) {
        p.mesh.visible = false
        this.particles.splice(i, 1)
        continue
      }

      // Move
      p.mesh.position.add(p.velocity.clone().multiplyScalar(dt))

      // Gravity
      p.velocity.y -= 8 * dt

      // Fade out
      const t = p.life / p.maxLife
      const mat = p.mesh.material as THREE.MeshBasicMaterial
      mat.opacity = t * 0.7

      // Shrink slightly
      const scale = 0.5 + t * 0.5
      p.mesh.scale.setScalar(scale)
    }
  }

  private spawnParticle(
    x: number, y: number, z: number,
    steerDir: number, speed: number
  ): void {
    // Find an available mesh from the pool
    const mesh = this.particlePool.find(m => !m.visible)
    if (!mesh) return

    mesh.visible = true
    // Spawn at skier's feet, slightly behind
    mesh.position.set(
      x + (Math.random() - 0.5) * 0.3,
      y + 0.1 + Math.random() * 0.2,
      z - 0.3 - Math.random() * 0.3
    )

    // Spray opposite to turn direction and upward
    const sprayX = -steerDir * (SPRAY_SPEED + Math.random() * 2)
    const sprayY = 1.5 + Math.random() * 2 + speed * 0.08
    const sprayZ = -1 + Math.random() * 0.5  // slightly backward

    const life = PARTICLE_LIFE * (0.7 + Math.random() * 0.3)

    this.particles.push({
      mesh,
      velocity: new THREE.Vector3(sprayX, sprayY, sprayZ),
      life,
      maxLife: life,
    })
  }
}
